import {
  Badge,
  Banner,
  BlockStack,
  Box,
  Button,
  Card,
  Checkbox,
  Divider,
  InlineStack,
  Layout,
  Modal,
  Page,
  RadioButton,
  SkeletonBodyText,
  Text,
  TextField,
} from "@shopify/polaris";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { useVerifiedShopGeneration } from "../ShopGenerationBoundary";
import { ApiError } from "../api/client";
import {
  getPurchaseOrderEmailDelivery,
  listPurchaseOrderEmailDeliveries,
  purchaseOrderEmailReconciliationKeys,
  reconcilePurchaseOrderEmailDelivery,
  type PurchaseOrderEmailDelivery,
  type PurchaseOrderEmailReconciliationDecision,
} from "../api/purchase_order_email_reconciliation";
import type { PurchaseOrderEmailPreview } from "../api/purchase_orders";
import {
  canonicalStockyPublicBigIntId,
  isStockyPublicBigIntId,
  type StockyPublicBigIntId,
} from "../api/stocky_public_ids";
import { t } from "../lib/i18n";
import { formatDateTime } from "../utils/date";
import {
  clearPurchaseOrderEmailAttempt,
  findPurchaseOrderEmailAttemptForSupplier,
  rememberPurchaseOrderEmailAttempt,
} from "../utils/purchaseOrderEmailAttempt";

const RECONCILIATION_WAIT_MS = 24 * 60 * 60 * 1000;

function emailStatus(delivery: PurchaseOrderEmailDelivery) {
  if (delivery.reconciliation_decision === "sent") {
    return <Badge tone="success">{t("送信済みで確定")}</Badge>;
  }
  if (delivery.reconciliation_decision === "not_sent") {
    return <Badge tone="info">{t("未送信で確定")}</Badge>;
  }
  if (delivery.status === "unknown") {
    return <Badge tone="critical">{t("要照合")}</Badge>;
  }
  if (delivery.status === "in_flight") {
    return <Badge tone="warning">{t("送信処理中")}</Badge>;
  }
  if (delivery.status === "pending") {
    return <Badge tone="attention">{t("送信受付済み")}</Badge>;
  }
  if (delivery.status === "failed" && delivery.uncertain_at !== null) {
    return <Badge tone="warning">{t("送信結果が不明")}</Badge>;
  }
  if (delivery.status === "cancelled") {
    return <Badge>{t("受付期限切れ")}</Badge>;
  }
  if (delivery.status === "succeeded") {
    return <Badge tone="success">{t("送信済み")}</Badge>;
  }
  return <Badge tone="info">{t("送信失敗")}</Badge>;
}

function errorText(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 401) {
      return t(
        "セッションを確認できませんでした（401）。Shopify 管理画面からアプリを再読み込みしてください。",
      );
    }
    if (error.status === 404) {
      return t(
        "このストアで確認できる発注書メールが見つかりませんでした（404）。一覧から開き直してください。",
      );
    }
    if (error.status === 409) {
      return t(
        "発注書メールの状態が変わったか、24時間の安全待機中です（409）: {detail}",
        {
          detail: error.detail,
        },
      );
    }
    if (error.status === 422) {
      return t("入力内容を確認してください（422）: {detail}", {
        detail: error.detail,
      });
    }
    return t("発注書メールの照合に失敗しました（{status}）: {detail}", {
      status: error.status,
      detail: error.detail,
    });
  }
  return error instanceof Error
    ? error.message
    : t("発注書メールの照合に失敗しました。");
}

function providerPreview(
  delivery: PurchaseOrderEmailDelivery,
): PurchaseOrderEmailPreview {
  return {
    draft_sha: delivery.draft_sha,
    supplier_id: delivery.supplier_id,
    location_id: delivery.location_id,
    input_epoch: delivery.input_epoch,
    from_email: delivery.from_email,
    to_email: delivery.to_email,
    subject: delivery.subject,
    text: delivery.text,
    purchase_order_note: delivery.purchase_order_note,
  };
}

function requestedLocationText(delivery: PurchaseOrderEmailDelivery): string {
  return delivery.requested_location_id === null
    ? t("送信依頼時の主拠点（自動選択）")
    : t("拠点ID {id} を明示指定", {
        id: delivery.requested_location_id,
      });
}

function resolvedLocationText(delivery: PurchaseOrderEmailDelivery): string {
  return delivery.location_id === null
    ? t("保存された拠点なし")
    : t("拠点ID {id}", { id: delivery.location_id });
}

function canResume(delivery: PurchaseOrderEmailDelivery): boolean {
  return (
    delivery.reconciliation_decision === null &&
    (delivery.status === "pending" ||
      delivery.status === "in_flight" ||
      (delivery.status === "failed" && delivery.uncertain_at !== null))
  );
}

function hasValidDeliveryOverride(
  delivery: PurchaseOrderEmailDelivery,
): boolean {
  return delivery.new_order_after_delivery_id === null
    ? true
    : delivery.confirmation_token === "send_new_purchase_order";
}

function isReconciliationReady(
  delivery: PurchaseOrderEmailDelivery,
  now: number,
): boolean {
  if (
    delivery.status !== "unknown" ||
    delivery.reconciliation_decision !== null ||
    delivery.idempotency_started_at === null
  ) {
    return false;
  }
  const startedAt = new Date(delivery.idempotency_started_at).getTime();
  return (
    Number.isFinite(startedAt) && now >= startedAt + RECONCILIATION_WAIT_MS
  );
}

function PurchaseOrderEmailReconciliationList() {
  const navigate = useNavigate();
  const [includeReconciled, setIncludeReconciled] = useState(false);
  const query = useQuery({
    queryKey: [
      ...purchaseOrderEmailReconciliationKeys.all,
      "list",
      includeReconciled,
    ],
    queryFn: () => listPurchaseOrderEmailDeliveries(includeReconciled),
  });

  return (
    <Page
      title={t("発注書メールの送信確認")}
      subtitle={t("送信結果が確定していないメールを、二重送信せずに確認します")}
    >
      <Layout>
        <Layout.Section>
          <BlockStack gap="400">
            <Banner
              tone="warning"
              title={t("新しいメールとして送り直さないでください")}
            >
              {t(
                "一覧に残るメールは、送信受付後の通信切断などで結果を確定できていません。安全な再開は保存済みの同じ本文・同じ操作キーだけで行います。",
              )}
            </Banner>
            <Checkbox
              label={t("照合済みの記録も表示")}
              checked={includeReconciled}
              onChange={setIncludeReconciled}
            />
            {query.isLoading ? (
              <Card>
                <SkeletonBodyText lines={5} />
              </Card>
            ) : query.isError ? (
              <Banner
                tone="critical"
                title={t("発注書メールの一覧を取得できませんでした")}
                action={{
                  content: t("再試行"),
                  onAction: () => void query.refetch(),
                }}
              >
                {errorText(query.error)}
              </Banner>
            ) : (query.data?.deliveries.length ?? 0) === 0 ? (
              <Banner
                tone="success"
                title={t("確認が必要な発注書メールはありません")}
              >
                {t("送信結果が不明な発注書メールはありません。")}
              </Banner>
            ) : (
              query.data?.deliveries.map((delivery) => (
                <Card key={delivery.id}>
                  <InlineStack
                    align="space-between"
                    blockAlign="center"
                    gap="300"
                  >
                    <BlockStack gap="100">
                      <InlineStack gap="200" blockAlign="center">
                        <Text as="h2" variant="headingMd">
                          {t("仕入先ID {id} への発注書メール", {
                            id: delivery.supplier_id,
                          })}
                        </Text>
                        {emailStatus(delivery)}
                      </InlineStack>
                      <Text as="p" tone="subdued">
                        {delivery.to_email} ·{" "}
                        {formatDateTime(delivery.accepted_at)}
                      </Text>
                      <Text as="p" tone="subdued">
                        {t(
                          "要求した配送先: {requested} / 保存した配送先: {resolved}",
                          {
                            requested: requestedLocationText(delivery),
                            resolved: resolvedLocationText(delivery),
                          },
                        )}
                      </Text>
                    </BlockStack>
                    <Button
                      variant="primary"
                      onClick={() =>
                        navigate(
                          `/purchase-order-email-reconciliation/${delivery.id}`,
                        )
                      }
                    >
                      {t("内容を確認")}
                    </Button>
                  </InlineStack>
                </Card>
              ))
            )}
          </BlockStack>
        </Layout.Section>
      </Layout>
    </Page>
  );
}

function PurchaseOrderEmailReconciliationDetail({
  deliveryId,
  shopGeneration,
}: {
  deliveryId: StockyPublicBigIntId;
  shopGeneration: string;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [decision, setDecision] =
    useState<PurchaseOrderEmailReconciliationDecision | null>(null);
  const [note, setNote] = useState("");
  const [observedAt, setObservedAt] = useState("");
  const [reference, setReference] = useState("");
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [resumeError, setResumeError] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const submissionLock = useRef(false);

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(id);
  }, []);

  const query = useQuery({
    queryKey: purchaseOrderEmailReconciliationKeys.detail(deliveryId),
    queryFn: () => getPurchaseOrderEmailDelivery(deliveryId),
  });
  const delivery = query.data;

  useEffect(() => {
    if (!delivery) return;
    setDecision(delivery.reconciliation_decision);
    setNote(delivery.reconciliation_note ?? "");
    const evidence = delivery.reconciliation_evidence;
    setObservedAt(
      typeof evidence?.observed_at === "string" ? evidence.observed_at : "",
    );
    setReference(
      typeof evidence?.reference === "string" ? evidence.reference : "",
    );
    setConfirmationOpen(false);
  }, [delivery?.id, delivery?.reconciliation_decision]);

  const mutation = useMutation({
    mutationFn: (input: {
      decision: PurchaseOrderEmailReconciliationDecision;
      confirmed: true;
      note: string;
      evidence: {
        source: string;
        observed_at: string;
        reference: string;
      };
    }) => reconcilePurchaseOrderEmailDelivery(deliveryId, input),
    onSuccess: (result) => {
      setConfirmationOpen(false);
      queryClient.setQueryData(
        purchaseOrderEmailReconciliationKeys.detail(result.id),
        result,
      );
      const pending = findPurchaseOrderEmailAttemptForSupplier(
        shopGeneration,
        result.supplier_id,
      );
      if (
        pending?.key === result.idempotency_key &&
        pending.preview.draft_sha === result.draft_sha
      ) {
        clearPurchaseOrderEmailAttempt(pending);
      }
      void queryClient.invalidateQueries({
        queryKey: [...purchaseOrderEmailReconciliationKeys.all, "list"],
      });
      void queryClient.invalidateQueries({
        queryKey: purchaseOrderEmailReconciliationKeys.unresolved,
      });
    },
    onSettled: () => {
      submissionLock.current = false;
    },
  });

  const ready = delivery ? isReconciliationReady(delivery, now) : false;
  const canOpenConfirmation =
    ready &&
    decision !== null &&
    note.trim() !== "" &&
    observedAt.trim() !== "" &&
    reference.trim() !== "" &&
    !mutation.isPending;

  const submit = () => {
    if (!decision || !canOpenConfirmation || submissionLock.current) return;
    submissionLock.current = true;
    mutation.mutate({
      decision,
      confirmed: true,
      note: note.trim(),
      evidence: {
        source: "supplier_or_ureyukibox_support_confirmation",
        observed_at: observedAt.trim(),
        reference: reference.trim(),
      },
    });
  };

  const resume = () => {
    if (!delivery) return;
    const preview = providerPreview(delivery);
    try {
      rememberPurchaseOrderEmailAttempt({
        shopGeneration,
        supplierId: delivery.supplier_id,
        requestedLocationId: delivery.requested_location_id,
        preview,
        idempotencyKey: delivery.idempotency_key,
        priorDeliveryId: delivery.new_order_after_delivery_id,
        confirmationToken:
          delivery.new_order_after_delivery_id === null
            ? null
            : delivery.confirmation_token,
      });
      setResumeError(null);
    } catch {
      setResumeError(
        t(
          "保存済みの宛先・本文・操作キーをこのタブに安全に保存できなかったため、送信は再開していません。ブラウザのストレージ設定を確認してください。",
        ),
      );
      return;
    }
    const params = new URLSearchParams({
      supplier: String(delivery.supplier_id),
    });
    if (delivery.requested_location_id !== null) {
      params.set("location", String(delivery.requested_location_id));
    }
    navigate(`/purchase-orders?${params.toString()}`);
  };

  const preview = delivery ? providerPreview(delivery) : null;
  const finalDecision = decision;

  return (
    <Page
      title={t("発注書メールの送信確認")}
      backAction={{
        content: t("発注書メールの未解決一覧へ"),
        onAction: () => navigate("/purchase-order-email-reconciliation"),
      }}
    >
      <Modal
        open={confirmationOpen}
        onClose={() => {
          if (!mutation.isPending) setConfirmationOpen(false);
        }}
        title={
          finalDecision === "sent"
            ? t("「送信済み」で確定しますか？")
            : t("「未送信」で確定しますか？")
        }
        primaryAction={{
          content:
            finalDecision === "sent"
              ? t("送信済みと確定する")
              : t("未送信と確定する"),
          destructive: finalDecision === "not_sent",
          loading: mutation.isPending,
          disabled: mutation.isPending,
          onAction: submit,
        }}
        secondaryActions={[
          {
            content: t("戻って確認する"),
            disabled: mutation.isPending,
            onAction: () => setConfirmationOpen(false),
          },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="300">
            <Text as="p">
              {finalDecision === "sent"
                ? t(
                    "仕入先から受信確認を得た場合、または UreyukiBox サポートから送信済みと回答された場合だけ確定してください。",
                  )
                : t(
                    "このメールが実際には届いていた場合、新しく送り直すと二重送信になります。仕入先の受信状況または UreyukiBox サポートの回答をもう一度確認してください。",
                  )}
            </Text>
            <Text as="p" fontWeight="semibold">
              {t("確定後はこの送信を同じ操作キーで再開できません。")}
            </Text>
          </BlockStack>
        </Modal.Section>
      </Modal>
      <Layout>
        <Layout.Section>
          <BlockStack gap="400">
            {query.isLoading ? (
              <Card>
                <SkeletonBodyText lines={10} />
              </Card>
            ) : query.isError ? (
              <Banner
                tone="critical"
                title={t("発注書メールの詳細を取得できませんでした")}
                action={{
                  content: t("再試行"),
                  onAction: () => void query.refetch(),
                }}
              >
                {errorText(query.error)}
              </Banner>
            ) : delivery ? (
              <>
                {delivery.status === "unknown" &&
                  delivery.reconciliation_decision === null && (
                    <Banner
                      tone="critical"
                      title={t("仕入先にメールの受信を確認してください")}
                      action={{
                        content: t("確認できない場合は問い合わせる"),
                        onAction: () => navigate("/contact"),
                      }}
                    >
                      {ready
                        ? t(
                            "下に保存された宛先・件名を伝え、電話など別の連絡手段で仕入先に届いたか確認してください。結果を確認できない場合は推測せず、お問い合わせください。",
                          )
                        : t(
                            "まだ24時間の安全待機中です。新しいメールとして送り直さず、送信開始から24時間後に状態を再取得してください。",
                          )}
                    </Banner>
                  )}
                {canResume(delivery) && (
                  <Banner
                    tone="warning"
                    title={t("保存済みの同じ送信だけを再開できます")}
                    action={
                      preview === null || !hasValidDeliveryOverride(delivery)
                        ? undefined
                        : { content: t("同じ送信を再開"), onAction: resume }
                    }
                  >
                    {preview === null || !hasValidDeliveryOverride(delivery)
                      ? t(
                          "保存済み本文を読み取れません。再送せずサポートへ連絡してください。",
                        )
                      : t(
                          "宛先・件名・本文・操作キーを変えずに注文書画面へ戻します。新しいメールは作りません。",
                        )}
                  </Banner>
                )}

                {resumeError !== null && (
                  <Banner
                    tone="critical"
                    title={t("保存済みの送信を再開できませんでした")}
                  >
                    {resumeError}
                  </Banner>
                )}

                {mutation.isError && (
                  <Banner
                    tone="critical"
                    title={t("照合処理を完了できませんでした")}
                    action={{
                      content: t("状態を再取得"),
                      onAction: () => void query.refetch(),
                    }}
                  >
                    {errorText(mutation.error)}
                  </Banner>
                )}

                <Card>
                  <BlockStack gap="400">
                    <InlineStack
                      align="space-between"
                      blockAlign="center"
                      gap="200"
                    >
                      <BlockStack gap="100">
                        <Text as="h2" variant="headingLg">
                          {t("仕入先ID {id} への発注書メール", {
                            id: delivery.supplier_id,
                          })}
                        </Text>
                        <Text as="p" tone="subdued">
                          {t("送信受付: {value}", {
                            value: formatDateTime(delivery.accepted_at),
                          })}
                        </Text>
                      </BlockStack>
                      {emailStatus(delivery)}
                    </InlineStack>
                    <Divider />
                    <BlockStack gap="150">
                      <Text as="h3" variant="headingSm">
                        {t("配送先の保存内容")}
                      </Text>
                      <Text as="p">
                        {t("要求した配送先: {value}", {
                          value: requestedLocationText(delivery),
                        })}
                      </Text>
                      <Text as="p">
                        {t("送信本文に確定した配送先: {value}", {
                          value: resolvedLocationText(delivery),
                        })}
                      </Text>
                    </BlockStack>
                    <Divider />
                    {preview === null ? (
                      <Banner tone="critical">
                        {t(
                          "保存済み本文を表示できません。判断せずサポートへ連絡してください。",
                        )}
                      </Banner>
                    ) : (
                      <BlockStack gap="300">
                        <Text as="h3" variant="headingSm">
                          {t("送信受付時に保存した全文")}
                        </Text>
                        <Text as="p">
                          <Text as="span" fontWeight="semibold">
                            {t("宛先")}:
                          </Text>{" "}
                          {preview.to_email}
                        </Text>
                        <Text as="p">
                          <Text as="span" fontWeight="semibold">
                            {t("差出人")}:
                          </Text>{" "}
                          {preview.from_email}
                        </Text>
                        <Text as="p">
                          <Text as="span" fontWeight="semibold">
                            {t("件名")}:
                          </Text>{" "}
                          {preview.subject}
                        </Text>
                        <Box
                          background="bg-surface-secondary"
                          borderRadius="300"
                          padding="400"
                        >
                          <pre
                            style={{
                              margin: 0,
                              whiteSpace: "pre-wrap",
                              overflowWrap: "anywhere",
                              fontFamily: "inherit",
                            }}
                          >
                            {preview.text}
                          </pre>
                        </Box>
                      </BlockStack>
                    )}
                    {delivery.has_last_error && (
                      <Banner
                        tone="warning"
                        title={t("最後に記録した送信エラー")}
                      >
                        {t(
                          "メール配信サービスから送信結果を確認できなかったため、この画面で確認が必要です。",
                        )}
                      </Banner>
                    )}
                  </BlockStack>
                </Card>

                {delivery.status === "unknown" &&
                  delivery.reconciliation_decision === null && (
                    <Card>
                      <BlockStack gap="400">
                        <Text as="h2" variant="headingMd">
                          {t("確認した送信結果と証跡を入力")}
                        </Text>
                        <Text as="p" tone="subdued">
                          {t(
                            "選択しただけでは確定しません。必須項目を入力して確認画面を開き、もう一度内容を確認します。",
                          )}
                        </Text>
                        <BlockStack gap="200">
                          <RadioButton
                            id="purchase-order-email-sent"
                            name="purchase-order-email-decision"
                            label={t("送信済み")}
                            helpText={t(
                              "仕入先の受信確認、または UreyukiBox サポートの送信済み回答があります。",
                            )}
                            checked={decision === "sent"}
                            disabled={!ready}
                            onChange={(checked) =>
                              checked && setDecision("sent")
                            }
                          />
                          <RadioButton
                            id="purchase-order-email-not-sent"
                            name="purchase-order-email-decision"
                            label={t("未送信")}
                            helpText={t(
                              "仕入先の未受信確認、または UreyukiBox サポートの未送信回答があります。",
                            )}
                            checked={decision === "not_sent"}
                            disabled={!ready}
                            onChange={(checked) =>
                              checked && setDecision("not_sent")
                            }
                          />
                        </BlockStack>
                        <TextField
                          label={t("確認メモ（必須）")}
                          value={note}
                          onChange={setNote}
                          multiline={3}
                          maxLength={2000}
                          showCharacterCount
                          disabled={!ready}
                          autoComplete="off"
                          placeholder={t(
                            "例: 仕入先へ電話し、受信済みと確認しました",
                          )}
                        />
                        <TextField
                          label={t("確認した日時（必須）")}
                          value={observedAt}
                          onChange={setObservedAt}
                          maxLength={255}
                          disabled={!ready}
                          autoComplete="off"
                          placeholder="2026-08-10 14:30 JST"
                        />
                        <TextField
                          label={t("確認方法・根拠（必須）")}
                          value={reference}
                          onChange={setReference}
                          multiline={2}
                          maxLength={8000}
                          showCharacterCount
                          disabled={!ready}
                          autoComplete="off"
                          placeholder={t("例: 仕入先担当者○○様へ電話で確認")}
                        />
                        <InlineStack align="end">
                          <Button
                            variant="primary"
                            disabled={!canOpenConfirmation}
                            loading={mutation.isPending}
                            onClick={() => setConfirmationOpen(true)}
                          >
                            {t("確認画面へ")}
                          </Button>
                        </InlineStack>
                      </BlockStack>
                    </Card>
                  )}

                {delivery.reconciliation_decision !== null && (
                  <Banner
                    tone="success"
                    title={t("この発注書メールの照合は完了しています")}
                    action={{
                      content: t("注文書を開く"),
                      onAction: () => navigate("/purchase-orders"),
                    }}
                  >
                    {delivery.reconciliation_decision === "sent"
                      ? t(
                          "送信済みとして確定しました。このメールを再送する必要はありません。",
                        )
                      : t(
                          "未送信として確定しました。必要なら注文書画面で最新内容を確認し、新しいメールとして送信できます。",
                        )}
                  </Banner>
                )}
              </>
            ) : null}
          </BlockStack>
        </Layout.Section>
      </Layout>
    </Page>
  );
}

export function PurchaseOrderEmailReconciliation() {
  const params = useParams<{ deliveryId?: string }>();
  const { shopGeneration } = useVerifiedShopGeneration();
  if (params.deliveryId === undefined) {
    return <PurchaseOrderEmailReconciliationList />;
  }
  if (!isStockyPublicBigIntId(params.deliveryId)) {
    return (
      <Page title={t("発注書メールの送信確認")}>
        <Banner tone="critical">
          {t(
            "発注書メールIDが正しくありません。未解決の一覧から開き直してください。",
          )}
        </Banner>
      </Page>
    );
  }
  const deliveryId = canonicalStockyPublicBigIntId(params.deliveryId);
  return (
    <PurchaseOrderEmailReconciliationDetail
      deliveryId={deliveryId}
      shopGeneration={shopGeneration}
    />
  );
}
